import { useEffect, useState } from "react";
import { Clock3, Check, X, Link2, ExternalLink, AlertTriangle, Star, Calendar, MessageSquare, PenLine, FileText } from "lucide-react";
import { http, fmtDate, fmtDateTime, fmtRupee, errMsg, timeLeft, downloadFile } from "../api";
import AudioPlayer from "../components/AudioPlayer";
import OrderFiles from "../components/OrderFiles";
import AgreementSignModal from "../components/AgreementSignModal";

function ReviewForm({ orderId, onDone }) {
  const [rating, setRating] = useState(5);
  const [review, setReview] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await http.post(`/orders/${orderId}/review`, { rating, review }); onDone(); }
    catch (e) { alert(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div style={{ padding: 12, background: "var(--surface-2)", borderRadius: 10, marginTop: 10 }}>
      <div style={{ display: "flex", gap: 4, marginBottom: 8 }}>
        {[1, 2, 3, 4, 5].map(n => (
          <button key={n} onClick={() => setRating(n)} data-testid={`rate-${n}`}>
            <Star size={20} color="var(--warning)" fill={n <= rating ? "var(--warning)" : "none"} />
          </button>
        ))}
      </div>
      <textarea rows={2} placeholder="Optional review..." value={review} onChange={e => setReview(e.target.value)} />
      <button className="primary" style={{ marginTop: 8 }} onClick={submit} disabled={busy} data-testid="submit-review">Submit rating</button>
    </div>
  );
}

function OrderCard({ o, user, reload }) {
  const [showDetail, setShowDetail] = useState(false);
  const [deliveries, setDeliveries] = useState([]);
  const [driveLink, setDriveLink] = useState("");
  const [notes, setNotes] = useState("");
  const [actualDuration, setActualDuration] = useState("");
  const [busy, setBusy] = useState(false);
  const [action, setAction] = useState(null); // "revision" | "deadline" | "review"
  const [reason, setReason] = useState("");
  const [newDeadline, setNewDeadline] = useState("");
  const [messageLink, setMessageLink] = useState("");
  const [messages, setMessages] = useState([]);
  const [signing, setSigning] = useState(false);
  const [signedDocs, setSignedDocs] = useState([]);

  const loadDetail = () => http.get(`/orders/${o.id}`).then(r => setDeliveries(r.data.deliveries || []));
  useEffect(() => { if (showDetail) { loadDetail(); http.get(`/orders/${o.id}/messages`).then(r => setMessages(r.data)).catch(() => {}); if (o.agreements_total) http.get(`/orders/${o.id}/agreements`).then(r => setSignedDocs(r.data.filter(a => a.signed))).catch(() => {}); } }, [showDetail]);
  const pendingAgreements = o.agreements_pending || 0;

  const act = async (path, body = {}) => {
    setBusy(true);
    try { await http.post(`/orders/${o.id}/${path}`, body); reload(); if (showDetail) loadDetail(); }
    catch (e) { alert(errMsg(e)); } finally { setBusy(false); setAction(null); setReason(""); setNewDeadline(""); setMessageLink(""); }
  };
  const deliver = async () => {
    if (!driveLink) return;
    setBusy(true);
    const fd = new FormData(); fd.append("drive_link", driveLink); fd.append("notes", notes); fd.append("actual_duration_hours", actualDuration);
    try { await http.post(`/orders/${o.id}/deliver`, fd, { headers: { "Content-Type": "multipart/form-data" } }); reload(); loadDetail(); setDriveLink(""); setNotes(""); setActualDuration(""); }
    catch (e) { alert(errMsg(e)); } finally { setBusy(false); }
  };

  const ackLeft = o.status === "PENDING_ARTIST_ACKNOWLEDGEMENT" ? timeLeft(o.ack_deadline) : null;
  const dlLeft = ["IN_PROGRESS", "REVISION_REQUESTED"].includes(o.status) ? timeLeft(o.deadline) : null;
  const statusTone = o.status === "COMPLETED" ? "success" : o.status === "DECLINED" || o.status === "CANCELLED" ? "danger" : o.status === "DELIVERED" ? "info" : "warning";
  const hasDeadlineRevision = o.deadline_revision_status === "REQUESTED";

  return (
    <div className={`list-row ${hasDeadlineRevision ? "danger-border" : ""}`} style={{ flexDirection: "column", alignItems: "stretch", gap: 12 }} data-testid={`order-row-${o.id}`}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
        <div>
          <div style={{ fontSize: 11, color: "var(--muted)", fontWeight: 600, letterSpacing: ".05em" }}>{o.order_code}</div>
          <h3 style={{ marginTop: 3 }}>{o.project_title}</h3>
          <p>{user.role === "client" ? `Voice: ${o.artist_name} (${o.language}) · ${o.estimated_hours}h · ${fmtRupee(o.final_amount ?? o.estimated_amount)}` : user.role === "artist" ? `Client: ${o.client_name} · Your base rate ${fmtRupee(o.artist_base_rate)} / h · ${o.duration_is_final ? "Final" : "Estimated"} payout ${fmtRupee(o.artist_payout)}` : `${o.client_name} → ${o.artist_name}`}</p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
          <span className={`badge ${statusTone}`}>{o.status.replaceAll("_", " ")}</span>
          {ackLeft && <span className={`badge ${ackLeft.tone}`}><Clock3 size={11} /> Ack {ackLeft.text}</span>}
          {dlLeft && !hasDeadlineRevision && <span className={`badge ${dlLeft.tone}`}><Calendar size={11} /> Due {fmtDate(o.deadline)} · {dlLeft.text}</span>}
        </div>
      </div>

      {hasDeadlineRevision && (
        <div className="deadline-alert" data-testid={`deadline-alert-${o.id}`}>
          <h3><AlertTriangle size={15} /> Deadline revision requested</h3>
          <p>Original: <strong>{fmtDateTime(o.original_deadline || o.deadline)}</strong> → Requested: <strong>{fmtDateTime(o.requested_deadline)}</strong><br />Reason: {o.deadline_revision_reason}</p>
          {(user.role === "client" || user.role === "admin") && (
            <div className="deadline-alert-actions">
              <button className="pill-action approve" onClick={() => act("deadline-approve")} disabled={busy} data-testid={`approve-deadline-${o.id}`}><Check size={13} /> Approve</button>
              <button className="pill-action reject" onClick={() => act("deadline-reject")} disabled={busy} data-testid={`reject-deadline-${o.id}`}><X size={13} /> Reject</button>
            </div>
          )}
        </div>
      )}

      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {user.role === "artist" && o.status === "PENDING_ARTIST_ACKNOWLEDGEMENT" && (
          <>
            {pendingAgreements > 0 && (
              <button className="pill-action neutral agreement-cta" onClick={() => setSigning(true)} data-testid={`sign-agreements-${o.id}`}><PenLine size={13} /> Sign {pendingAgreements} agreement{pendingAgreements > 1 ? "s" : ""} first</button>
            )}
            <button className="pill-action approve" onClick={() => act("accept")} disabled={busy || pendingAgreements > 0} title={pendingAgreements ? "Sign the client's agreements before accepting" : ""} data-testid={`accept-order-${o.id}`}><Check size={13} /> Accept order</button>
            <button className="pill-action reject" onClick={() => setAction("decline")} disabled={busy} data-testid={`decline-order-${o.id}`}><X size={13} /> Decline</button>
          </>
        )}
        {user.role === "artist" && ["IN_PROGRESS", "REVISION_REQUESTED"].includes(o.status) && !hasDeadlineRevision && (
          <button className="pill-action neutral" onClick={() => setAction("deadline")} data-testid={`request-deadline-${o.id}`}><Calendar size={13} /> Request deadline change</button>
        )}
        {user.role === "client" && o.status === "DELIVERED" && (
          <>
            <button className="pill-action approve" onClick={() => act("approve")} disabled={busy} data-testid={`approve-delivery-${o.id}`}><Check size={13} /> Approve delivery</button>
            <button className="pill-action reject" onClick={() => setAction("revision")} data-testid={`request-revision-${o.id}`}><MessageSquare size={13} /> Request revision</button>
          </>
        )}
        {user.role === "client" && o.status === "COMPLETED" && !o.reviewed && (
          <button className="pill-action neutral" onClick={() => setAction("review")} data-testid={`review-order-${o.id}`}><Star size={13} /> Rate voice</button>
        )}
        {(user.role === "client" || user.role === "artist") && !["COMPLETED", "DECLINED", "CANCELLED", "PENDING_ADMIN_REVIEW"].includes(o.status) && <button className="pill-action neutral" onClick={() => setAction("message")} data-testid={`new-order-message-${o.id}`}><MessageSquare size={13} /> Send project message</button>}
        <button className="ghost" onClick={() => setShowDetail(v => !v)} data-testid={`toggle-detail-${o.id}`}>{showDetail ? "Hide details" : "View details"}</button>
      </div>

      {action === "decline" && (
        <div style={{ padding: 10, background: "var(--surface-2)", borderRadius: 8 }}>
          <input placeholder="Reason for declining..." value={reason} onChange={e => setReason(e.target.value)} />
          <button className="primary" style={{ marginTop: 8 }} onClick={() => act("decline", { reason })} disabled={busy}>Confirm decline</button>
        </div>
      )}
      {action === "revision" && (
        <div style={{ padding: 10, background: "var(--surface-2)", borderRadius: 8 }}>
          <textarea placeholder="What needs to be revised?" rows={2} value={reason} onChange={e => setReason(e.target.value)} />
          <button className="primary" style={{ marginTop: 8 }} onClick={() => act("revision", { reason })} disabled={busy || !reason} data-testid={`confirm-revision-${o.id}`}>Send revision request</button>
        </div>
      )}
      {action === "deadline" && (
        <div style={{ padding: 10, background: "var(--surface-2)", borderRadius: 8 }}>
          <label>New deadline</label>
          <input type="datetime-local" value={newDeadline} onChange={e => setNewDeadline(e.target.value)} />
          <textarea placeholder="Reason for revision..." rows={2} value={reason} onChange={e => setReason(e.target.value)} style={{ marginTop: 8 }} />
          <button className="primary" style={{ marginTop: 8 }} onClick={() => act("deadline-revision", { requested_deadline: new Date(newDeadline).toISOString(), reason })} disabled={busy || !newDeadline || !reason} data-testid={`confirm-deadline-${o.id}`}>Submit request</button>
        </div>
      )}
      {action === "message" && <div style={{ padding: 10, background: "var(--surface-2)", borderRadius: 8 }}><textarea placeholder="Write your project question or request…" rows={2} value={reason} onChange={e => setReason(e.target.value)} data-testid={`order-message-text-${o.id}`} /><input type="url" placeholder="Optional secure material / Drive link" value={messageLink} onChange={e => setMessageLink(e.target.value)} style={{ marginTop: 8 }} data-testid={`order-message-link-${o.id}`} /><button className="primary" style={{ marginTop: 8 }} onClick={() => { const payload = { message: reason }; if (messageLink.trim()) payload.drive_link = messageLink.trim(); act("messages", payload); }} disabled={busy || !reason} data-testid={`send-order-message-${o.id}`}>Send message</button></div>}
      {action === "review" && <ReviewForm orderId={o.id} onDone={() => { setAction(null); reload(); }} />}
      {signing && <AgreementSignModal order={o} onClose={() => { setSigning(false); reload(); }} onAllSigned={() => reload()} />}

      {showDetail && (
        <div style={{ paddingTop: 12, borderTop: "1px dashed var(--line)" }} data-testid={`order-detail-${o.id}`}>
          <div className="detail-grid">
            <div>
              <div className="section-title" style={{ padding: "0 0 8px" }}>PROJECT DETAILS</div>
              <div className="kv"><span>Description</span><span>{o.description}</span></div>
              {o.instructions && <div className="kv"><span>Instructions</span><span>{o.instructions}</span></div>}
              <div className="kv"><span>Language</span><span>{o.language}</span></div>
              <div className="kv"><span>Category</span><span>{o.category}</span></div>
              <div className="kv"><span>Delivery format</span><span>{o.delivery_format || "MP3"}</span></div>
              <div className="kv"><span>Deadline</span><span>{fmtDateTime(o.deadline)}</span></div>
              <div className="kv"><span>{o.duration_is_final ? "Final duration" : "Estimated hours"}</span><span>{o.actual_duration_hours || o.estimated_hours}h</span></div>
              {user.role === "artist" ? <>
                <div className="kv"><span>Your base rate</span><span>{fmtRupee(o.artist_base_rate)} / hr</span></div>
                <div className="kv" data-testid={`artist-payout-${o.id}`}><span>{o.duration_is_final ? "Final payout" : "Estimated payout"}</span><span>{fmtRupee(o.artist_payout)}</span></div>
              </> : <>
                <div className="kv"><span>Rate</span><span>{fmtRupee(o.marketplace_rate_snapshot)} / hr</span></div>
                <div className="kv"><span>{o.actual_duration_hours ? "Final amount" : "Estimated amount"}</span><span>{fmtRupee(o.final_amount ?? o.estimated_amount)}</span></div>
              </>}
              <div style={{ marginTop: 12 }}><OrderFiles orderId={o.id} canUpload={user.role === "client" && !["COMPLETED", "DECLINED", "CANCELLED"].includes(o.status)} /></div>
              {signedDocs.length > 0 && (
                <>
                  <div className="section-title">SIGNED AGREEMENTS</div>
                  {signedDocs.map(a => (
                    <div key={a.id} className="list-row" style={{ padding: 10, marginBottom: 6 }}>
                      <FileText size={15} color="var(--success)" />
                      <div className="list-row-info"><strong>{a.title}</strong><p>Signed {fmtDateTime(a.signed_at)}</p></div>
                      <button className="ghost" onClick={() => downloadFile(`/files/${a.signed_id}`, {}, `${a.title}-signed.pdf`)} data-testid={`signed-doc-${a.id}`}>PDF</button>
                    </div>
                  ))}
                </>
              )}
              {deliveries.length > 0 && (
                <>
                  <div className="section-title">DELIVERIES</div>
                  {deliveries.map(d => (
                    <div key={d.id} className="list-row" style={{ padding: 10, marginBottom: 6 }}>
                      <div className="list-row-info">
                        <strong>Version {d.version}</strong>
                        <p>{d.notes || d.filename} · Final duration: {d.actual_duration_hours || o.actual_duration_hours || "—"}h</p>
                        <small style={{ color: "var(--muted)" }}>{fmtDateTime(d.created_at)}</small>
                      </div>
                      {d.drive_link ? <a className="ghost" href={d.drive_link} target="_blank" rel="noreferrer" data-testid={`delivery-drive-link-${d.id}`}><ExternalLink size={13} /> Open Drive link</a> : <AudioPlayer id={d.id} compact />}
                    </div>
                  ))}
                </>
              )}
              {messages.length > 0 && <><div className="section-title">PROJECT MESSAGES</div><div className="message-thread">{messages.map(message => <div className="message-bubble" key={message.id} data-testid={`order-message-${message.id}`}><strong>{message.sender_name}</strong><p>{message.payload?.message || message.payload?.reason || message.kind.replaceAll("_", " ")}</p>{message.payload?.drive_link && <a href={message.payload.drive_link} target="_blank" rel="noreferrer" className="ghost" data-testid={`order-message-link-open-${message.id}`}>Open shared link</a>}</div>)}</div></>}
              {user.role === "artist" && ["IN_PROGRESS", "REVISION_REQUESTED"].includes(o.status) && (
                <div style={{ marginTop: 12 }}>
                  <label>Google Drive delivery link</label>
                  <input type="url" placeholder="https://drive.google.com/..." value={driveLink} onChange={e => setDriveLink(e.target.value)} data-testid={`delivery-drive-link-input-${o.id}`} />
                  <input placeholder="Delivery notes (optional)" value={notes} onChange={e => setNotes(e.target.value)} style={{ marginTop: 8 }} data-testid={`delivery-notes-${o.id}`} />
                  <input type="number" min="0.01" step="0.01" placeholder="Final project duration (hours)" value={actualDuration} onChange={e => setActualDuration(e.target.value)} style={{ marginTop: 8 }} data-testid={`actual-duration-${o.id}`} />
                  <button className="primary" style={{ marginTop: 8 }} onClick={deliver} disabled={busy || !driveLink || !actualDuration || Number(actualDuration) <= 0} data-testid={`deliver-btn-${o.id}`}>
                    <Link2 size={14} /> {busy ? "Sending..." : "Send Drive delivery"}
                  </button>
                </div>
              )}
            </div>
            <div>
              <div className="section-title" style={{ padding: "0 0 8px" }}>ACTIVITY TIMELINE</div>
              <ul className="timeline">
                {(o.timeline || []).slice().reverse().map((t, i) => (
                  <li key={i}>
                    <span className="timeline-dot" />
                    <div className="timeline-body">
                      <strong>{t.event}</strong>
                      <small>{t.actor} · {fmtDateTime(t.created_at)}</small>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function OrdersPage({ user }) {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("all");
  const load = () => http.get("/orders").then(r => setOrders(r.data));
  useEffect(() => { load(); }, []);

  const filtered = orders.filter(o => {
    if (filter === "all") return true;
    if (filter === "active") return ["PENDING_ARTIST_ACKNOWLEDGEMENT", "IN_PROGRESS", "REVISION_REQUESTED", "DELIVERED"].includes(o.status);
    if (filter === "completed") return o.status === "COMPLETED";
    if (filter === "cancelled") return ["DECLINED", "CANCELLED"].includes(o.status);
    if (filter === "revisions") return o.deadline_revision_status === "REQUESTED";
    return true;
  });

  return (
    <>
      <div className="page-head">
        <div><h1>My Orders</h1><p>Every project, acknowledgement and delivery in one place.</p></div>
      </div>
      <div className="chip-row" style={{ marginBottom: 14 }}>
        {[["all", "All"], ["active", "Active"], ["completed", "Completed"], ["revisions", "Deadline requests"], ["cancelled", "Cancelled"]].map(([k, l]) => (
          <button key={k} className={`chip ${filter === k ? "active" : ""}`} onClick={() => setFilter(k)} data-testid={`filter-${k}`}>{l}</button>
        ))}
      </div>
      <div className="list-rows">
        {filtered.map(o => <OrderCard key={o.id} o={o} user={user} reload={load} />)}
        {filtered.length === 0 && (
          <div className="empty">
            <Clock3 size={30} />
            <div style={{ fontWeight: 600, marginBottom: 4 }}>No orders here yet</div>
            <div style={{ fontSize: 13 }}>{user.role === "client" ? "Browse the marketplace to hire your first voice." : "You will see new order requests here."}</div>
          </div>
        )}
      </div>
    </>
  );
}
